import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {ThemeProvider} from '@mui/material/styles';
import {darkTheme, lightTheme} from './Theme';

const STORAGE_KEY = 'theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

// 'auto' follows the OS setting, 'light'/'dark' pin it.
const MODES = ['auto', 'light', 'dark'];

const ThemeModeContext = createContext({ mode: 'auto', resolvedMode: 'light', setMode: () => {} });

export function useThemeMode() {
    return useContext(ThemeModeContext);
}

function readStoredMode() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return MODES.includes(stored) ? stored : 'auto';
    }
    catch (e) {
        // Safari private browsing can throw on localStorage access.
        return 'auto';
    }
}

function systemPrefersDark() {
    return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia(DARK_QUERY).matches;
}

export default function ThemeModeProvider(props) {
    const [mode, setModeState] = useState(readStoredMode);
    const [prefersDark, setPrefersDark] = useState(systemPrefersDark);

    // Track live OS changes so 'auto' flips without a reload.
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }
        const query = window.matchMedia(DARK_QUERY);
        const onChange = (e) => setPrefersDark(e.matches);

        // addEventListener is unsupported on MediaQueryList in older Safari.
        if (query.addEventListener) {
            query.addEventListener('change', onChange);
            return () => query.removeEventListener('change', onChange);
        }
        query.addListener(onChange);
        return () => query.removeListener(onChange);
    }, []);

    const resolvedMode = mode === 'auto' ? (prefersDark ? 'dark' : 'light') : mode;

    // Drive the plain-CSS custom properties in index.css, and tell the browser
    // to render native scrollbars and form controls to match.
    useEffect(() => {
        const root = document.documentElement;
        root.setAttribute('data-theme', resolvedMode);
        root.style.colorScheme = resolvedMode;
    }, [resolvedMode]);

    const setMode = useCallback((next) => {
        setModeState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        }
        catch (e) {
            // Preference just won't persist; the session still works.
        }
    }, []);

    const contextValue = useMemo(() => ({ mode, resolvedMode, setMode }), [mode, resolvedMode, setMode]);

    return (
        <ThemeModeContext.Provider value={contextValue}>
            {/* Deliberately no <CssBaseline/>: it would apply box-sizing:border-box
                and MUI typography globally, which this app's fixed-size layouts were
                never written against. index.css covers the body reset we actually need. */}
            <ThemeProvider theme={resolvedMode === 'dark' ? darkTheme : lightTheme}>
                {props.children}
            </ThemeProvider>
        </ThemeModeContext.Provider>
    );
}

export {MODES};
